import {
  Modal,
  Box,
  FormControl,
  FormLabel,
  Divider,
  Button,
  CircularProgress,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormHelperText,
} from "@mui/material";
import React from "react";
import { LabelStyle } from "../../Common/styles/form";
import {
  ModalDrawerStyles,
  ModalHeader,
  ModalDrawerButtonStyles,
} from "../../Common/styles/modal";
import { InputWrapper } from "../ParticipantDetails/Medication/style";
import { DrawerForm } from "../../Common/styles/drawer";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import { useParams } from "react-router-dom";
import http from "../../../utils/http";
import { Formik, Form } from "formik";
import * as yup from "yup";

type Props = {
  showModal: boolean;
  closeModal: () => void;
  refreshPage: () => void;
  editData?: any; // Note data for editing, undefined for creating new
};

const AddGlobalNoteModal = ({
  showModal,
  closeModal,
  refreshPage,
  editData,
}: Props) => {
  const { participantId } = useParams();

  const validationSchema = yup.object({
    note: yup
      .string()
      .required("Note is required")
      .min(1, "Note cannot be empty")
      .trim(),
    visibility: yup
      .string()
      .required("Visibility is required")
      .oneOf(["registry", "global"], "Invalid visibility option"),
  });

  const initialValues = {
    note: editData?.note || "",
    visibility: editData?.visibility || "",
  };

  const onSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      const body = {
        content: values.note.trim(),
        isPublic: values.visibility === "global",
        participantId: participantId,
      };

      let res;
      if (editData?.id) {
        res = await http.put(
          `/participant-notes/registry/${editData.id}`,
          body
        );
      } else {
        res = await http.post(`/participant-notes/registry`, body);
      }

      toastMessage("success", res.data.message);
      closeModal();
      refreshPage();
    } catch (error) {
      errorToastMessage(error as Error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={showModal} onClose={closeModal}>
      <Box sx={ModalDrawerStyles}>
        <ModalHeader
          title={editData?.id ? "Edit Note" : "Add Note"}
          onCloseClick={closeModal}
        />
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            isSubmitting,
          }) => (
            <Form style={DrawerForm}>
              <Box>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="note">
                    Note
                  </FormLabel>
                  <TextField
                    multiline
                    rows={4}
                    name="note"
                    value={values.note}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter your note here..."
                    variant="outlined"
                    fullWidth
                    error={touched.note && Boolean(errors.note)}
                  />
                  {touched.note && errors.note && (
                    <FormHelperText error>{String(errors.note)}</FormHelperText>
                  )}
                </FormControl>

                <FormControl sx={{ ...InputWrapper, mt: 2 }}>
                  <FormLabel sx={LabelStyle} htmlFor="visibility">
                    Visibility
                  </FormLabel>
                  <RadioGroup
                    name="visibility"
                    value={values.visibility}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="registry"
                      control={<Radio />}
                      label="Registry (Notes will be visible only in registry)"
                    />
                    <FormControlLabel
                      value="global"
                      control={<Radio />}
                      label="Global (Notes will be visible outside the registry and studies)"
                    />
                  </RadioGroup>
                  {touched.visibility && errors.visibility && (
                    <FormHelperText error>
                      {String(errors.visibility)}
                    </FormHelperText>
                  )}
                </FormControl>
              </Box>
              <Box sx={{ mt: "auto" }}>
                <Divider />
                <Box sx={ModalDrawerButtonStyles}>
                  {!isSubmitting ? (
                    <>
                      <Button onClick={closeModal} variant="outlined">
                        Cancel
                      </Button>
                      <Button type="submit" variant="contained">
                        Save
                      </Button>
                    </>
                  ) : (
                    <CircularProgress size={25} />
                  )}
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default AddGlobalNoteModal;
